import { createRequire } from 'node:module';
import path from 'node:path';
import { readdir } from 'node:fs/promises';
import { AbortError } from '@shopify/cli-kit/node/error';
import { outputWarn } from '@shopify/cli-kit/node/output';
import { fileExists } from '@shopify/cli-kit/node/fs';
import { muteRemixLogs } from './log.js';
import { REQUIRED_REMIX_VERSION } from './remix-version-check.js';
import { findFileWithExtension } from './file.js';
import { isViteProject, getViteConfig } from './vite-config.js';
import { importLocal, importVite } from './import-utils.js';
import { isHydrogenMonorepo, hydrogenPackagesPath } from './build.js';

async function hasRemixConfigFile(root) {
  const result = await findFileWithExtension(root, "remix.config");
  return !!result.filepath;
}
async function isClassicProject(root) {
  const isVite = await isViteProject(root);
  if (isVite) return false;
  return hasRemixConfigFile(root);
}
const BUILD_DIR = "dist";
const CLIENT_SUBDIR = "client";
const WORKER_SUBDIR = "worker";
const oxygenServerMainFields = ["browser", "module", "main"];
function getProjectPaths(appPath) {
  const root = appPath ?? process.cwd();
  const publicPath = path.join(root, "public");
  const buildPath = path.join(root, BUILD_DIR);
  const buildPathClient = path.join(buildPath, CLIENT_SUBDIR);
  const buildPathWorkerFile = path.join(buildPath, WORKER_SUBDIR, "index.js");
  return {
    root,
    buildPath,
    buildPathClient,
    buildPathWorkerFile,
    publicPath
  };
}
function handleRemixImportFail() {
  const remixVersion = REQUIRED_REMIX_VERSION;
  throw new AbortError(
    "Could not load Remix packages.",
    `Please make sure you have \`@remix-run/dev@${remixVersion}\` installed and all the other Remix packages have the same version.`
  );
}
function getRawRemixConfig(root) {
  return findFileWithExtension(root, "remix.config").then(({ filepath }) => {
    if (!filepath) throw new AbortError("No remix.config.js file found.");
    return createRequire(import.meta.url)(filepath);
  });
}
async function getRemixConfig(root, mode = process.env.NODE_ENV) {
  if (await isViteProject(root)) {
    return (await getViteConfig(root)).remixConfig;
  }
  await muteRemixLogs(root);
  const { resolveConfig } = await importLocal(
    "@remix-run/dev/dist/config.js",
    root
  ).catch(handleRemixImportFail);
  const { createContext } = await importLocal(
    "@remix-run/dev/dist/vite/vite-node.js",
    root
  ).catch(handleRemixImportFail);
  const appConfig = await getRawRemixConfig(root);
  const routesViteNodeContext = await createContext({ root, mode });
  const vite = await importVite(root);
  const config = await resolveConfig(appConfig, {
    rootDirectory: root,
    serverMode: mode,
    vite,
    routesViteNodeContext
  });
  if (isHydrogenMonorepo && hydrogenPackagesPath) {
    const packagesPath = hydrogenPackagesPath;
    config.watchPaths ??= [];
    config.watchPaths.push(
      ...(await readdir(packagesPath)).map(
        (pkg) => pkg === "hydrogen-react" ? path.resolve(packagesPath, pkg, "dist", "browser-dev", "index.mjs") : path.resolve(packagesPath, pkg, "dist", "development", "index.js")
      )
    );
    config.watchPaths.push(
      path.join(packagesPath, "cli", "dist", "virtual-routes", "**", "*")
    );
  }
  routesViteNodeContext.server.server.close();
  return config;
}
function assertOxygenChecks(config) {
  try {
    createRequire(import.meta.url).resolve("@shopify/remix-oxygen");
  } catch {
    return;
  }
  if (!config.serverEntryPoint) {
    throw new AbortError(
      "Could not find a server entry point.",
      "Please add a server option to your remix.config.js pointing to an Oxygen worker entry file."
    );
  } else {
    assertEntryFileExists(config.rootDirectory, config.serverEntryPoint);
  }
  if (config.serverPlatform !== "neutral") {
    throw new AbortError(
      'The serverPlatform in remix.config.js must be "neutral".'
    );
  }
  if (config.serverModuleFormat !== "esm") {
    throw new AbortError(
      'The serverModuleFormat in remix.config.js must be "esm".'
    );
  }
  if (config.serverDependenciesToBundle !== "all") {
    throw new AbortError(
      'The serverDependenciesToBundle in remix.config.js must be "all".'
    );
  }
  if (!config.serverConditions?.includes("worker")) {
    throw new AbortError(
      'The serverConditions in remix.config.js must include "worker".'
    );
  }
  if (process.env.NODE_ENV === "development" && !config.serverConditions?.includes("development")) {
    outputWarn(
      "Add `process.env.NODE_ENV` value to serverConditions in remix.config.js to enable debugging features in development."
    );
  }
  if (!config.serverMainFields || !oxygenServerMainFields.every((v, i) => config.serverMainFields?.[i] === v)) {
    throw new AbortError(
      `The serverMainFields in remix.config.js must be ${JSON.stringify(
        oxygenServerMainFields
      )}.`
    );
  }
  const cdnUrl = process.env.HYDROGEN_ASSET_BASE_URL;
  if (cdnUrl && !config.publicPath.startsWith(cdnUrl)) {
    throw new AbortError(
      "The publicPath in remix.config.js must be prepended with the value of `process.env.HYDROGEN_ASSET_BASE_URL`."
    );
  }
  const expectedServerBuildPath = path.join(
    BUILD_DIR,
    WORKER_SUBDIR,
    "index.js"
  );
  if (config.serverBuildPath !== path.resolve(config.rootDirectory, expectedServerBuildPath)) {
    throw new AbortError(
      `The serverBuildPath in remix.config.js must be "${expectedServerBuildPath}".`
    );
  }
  const expectedAssetsBuildDirectory = path.join(BUILD_DIR, CLIENT_SUBDIR);
  if (!config.assetsBuildDirectory.startsWith(
    path.resolve(config.rootDirectory, expectedAssetsBuildDirectory)
  )) {
    throw new AbortError(
      `The assetsBuildDirectory in remix.config.js must be in "${expectedAssetsBuildDirectory}".`
    );
  }
}
async function assertEntryFileExists(root, fileRelative) {
  const fileAbsolute = path.resolve(root, fileRelative);
  const exists = await fileExists(fileAbsolute);
  if (!exists) {
    if (!path.extname(fileAbsolute)) {
      const files = await readdir(path.dirname(fileAbsolute));
      const exists2 = files.some((file) => {
        const { name, ext } = path.parse(file);
        return name === path.basename(fileAbsolute) && /^\.[jt]s$/.test(ext);
      });
      if (exists2) return;
    }
    throw new AbortError(
      `Entry file "${fileRelative}" not found.`,
      "Please ensure the file exists and that the path is correctly added to the `server` property in remix.config.js."
    );
  }
}

export { assertOxygenChecks, getProjectPaths, getRawRemixConfig, getRemixConfig, handleRemixImportFail, hasRemixConfigFile, isClassicProject };
